import { PlatformHealthchecksStatusDto } from '@data/_generated-api/platform-api/models/platform-healthchecks-status-dto';
import { PlatformPlatformHealthStatusDto } from '@data/_generated-api/platform-api/models/platform-platform-health-status-dto';
import { PlatformUptimeRobotStatusDto } from '@data/_generated-api/platform-api/models/platform-uptime-robot-status-dto';

/** Statuskleur van een kaart: groen, oranje, rood of grijs (niet ingesteld). */
export type SystemStatusTone = 'ok' | 'warning' | 'danger' | 'muted';

/** Hoe het ophalen van een bron verliep (SystemStatusSourceState in de api). */
export type SystemStatusSourceState = 'Ok' | 'NotConfigured' | 'Rejected' | 'Unreachable';

export const UPTIME_ROBOT_DASHBOARD_URL = 'https://dashboard.uptimerobot.com/monitors';
export const HEALTHCHECKS_DASHBOARD_URL = 'https://healthchecks.io/checks/59dac19e-ab63-4a3e-b116-4b0a320a9220/details/';

/** Toon van een bron die niet Ok antwoordde, of undefined als ze wel antwoordde. */
function sourceTone(state: string | null | undefined): SystemStatusTone | undefined {
  switch (state) {
    case 'Ok':
      return undefined;
    case 'NotConfigured':
      return 'muted';
    case 'Rejected':
      return 'danger';
    default:
      return 'warning';
  }
}

export function uptimeRobotTone(status: PlatformUptimeRobotStatusDto | null | undefined): SystemStatusTone {
  const failed = sourceTone(status?.state);
  if (failed) {
    return failed;
  }
  const statuses = (status?.monitors ?? []).map(monitor => monitor.status);
  if (statuses.includes('Down')) {
    return 'danger';
  }
  // Gepauzeerde monitors tellen niet mee, tenzij er niets anders bewaakt wordt.
  const active = statuses.filter(s => s !== 'Paused');
  if (active.length === 0 || active.some(s => s !== 'Up')) {
    return 'warning';
  }
  return 'ok';
}

export function healthchecksTone(status: PlatformHealthchecksStatusDto | null | undefined): SystemStatusTone {
  const failed = sourceTone(status?.state);
  if (failed) {
    return failed;
  }
  switch (status?.check?.status) {
    case 'up':
      return 'ok';
    case 'down':
      return 'danger';
    default:
      // grace, paused, new: nog niet fout, wel opletten
      return 'warning';
  }
}

export function platformTone(status: PlatformPlatformHealthStatusDto | null | undefined): SystemStatusTone {
  if (status?.state !== 'Ok') {
    return 'danger';
  }
  return healthTone(status.status);
}

/** Toon van één health-check-uitkomst (Healthy, Degraded, Unhealthy). */
export function healthTone(status: string | null | undefined): SystemStatusTone {
  switch (status) {
    case 'Healthy':
      return 'ok';
    case 'Degraded':
      return 'warning';
    default:
      return 'danger';
  }
}

/** Toon van één UptimeRobot-monitor. */
export function monitorTone(status: string | null | undefined): SystemStatusTone {
  switch (status) {
    case 'Up':
      return 'ok';
    case 'Down':
      return 'danger';
    case 'Paused':
      return 'muted';
    default:
      return 'warning';
  }
}

/** Compacte duur, taalneutraal: "45 s", "12 min", "3 h 05 min", "2 d 4 h". */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return '';
  }
  if (seconds < 60) {
    return `${Math.round(seconds)} s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
  }
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
}
